import {
  Button,
  Card,
  EmptyState,
  InlineMessage,
  Menu,
  MenuCheckboxItem,
  MenuContent,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSub,
  MenuSubContent,
  MenuSubTrigger,
  MenuTrigger,
  MetricRow,
  PageContent,
  ScheduleIcon,
  Spinner,
  Tab,
  TabList,
  Tabs,
} from '@olytic/foundation';
import { ResourceGrid } from '@olytic/foundation/resourcing';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from '../auth/AuthProvider';
import { authenticatedFetch } from '../auth/authClient';
import { useDocumentTitle } from '../documentTitle';
import { DetailsAside } from './DetailsAside';
import {
  CalendarGlyph, CollapseAllGlyph, ExpandAllGlyph, PeopleGlyph, PlusGlyph, PrintGlyph, ProjectsGlyph, SettingsGlyph, TimelineGlyph,
} from './icons';
import {
  buildGrid,
  buildMetrics,
  buildPanel,
  cellOf,
  type Drag,
  focusLabel,
  hasAnything,
  hoursText,
  movedText,
  moveTargets,
  movingText,
  type NewForm,
  type PanelItem,
  Planner,
  type PersonSort,
  type ProjectSort,
  sameSelection,
  type Selection,
  selectionOf,
  type Settings,
  type TimeframeKey,
  timeframes,
  utcMonth,
  withHours,
  type View,
} from './model';
import { defaultForm, NewAssignmentDialog } from './NewAssignmentDialog';
import { deleteAssignment, fetchResourcing, reassignAssignment, type ResourcingData, updateAssignmentHours } from './resourcingApi';
import { loadPrefs, saveTab } from './viewPrefs';

/** What the banner under the toolbar says after a change, and how to take it back. */
type Done =
  | { kind: 'move'; text: string; id: string; previous: string | null; month: string }
  | { kind: 'hours'; text: string; id: string; previous: number }
  | { kind: 'add'; text: string; ids: string[] };

const VIEWS: [View, string, ReactNode][] = [
  ['people', 'People', <PeopleGlyph key="i" />],
  ['projects', 'Projects', <ProjectsGlyph key="i" />],
  ['timeline', 'Timeline', <TimelineGlyph key="i" />],
];

const PERSON_SORTS: [PersonSort, string][] = [['name', 'Name'], ['avg', 'Average utilization'], ['peak', 'Peak month'], ['free', 'Most hours available']];
const PROJECT_SORTS: [ProjectSort, string][] = [['start', 'Start date'], ['name', 'Account'], ['hours', 'Most hours'], ['unstaffed', 'Most unstaffed hours']];
const LOADING_METRICS = ['Capacity', 'Resourced', 'Unstaffed', 'Available', 'Utilization'].map((label) => ({ key: label, label, value: '—', sub: ' ' }));
const SORT_HINT = 'Across the months in view, or the month you select';

/**
 * Resource Planning, the Delivery app's planner: people, roles and projects
 * against months, from the stored resourcing roll-ups (GET /api/resourcing).
 * Click a month for its summary, a cell for its details; Reassign (or drag)
 * a month of work to someone who fills the role; New assignment adds hours.
 */
export function ResourcingPage(): JSX.Element {
  useDocumentTitle('Resource Planning');
  const navigate = useNavigate();
  const { email } = useCurrentUser();
  const [initial] = useState(() => {
    const prefs = loadPrefs(email);
    return prefs.tabs[prefs.view];
  });
  const [current, setCurrent] = useState(utcMonth);
  const [tf, setTf] = useState<TimeframeKey>(initial.timeframe);
  const [settings, setSettings] = useState<Settings>(initial.settings);
  const [data, setData] = useState<ResourcingData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [sel, setSel] = useState<Selection | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(initial.collapsed));
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(initial.expanded));
  const [drag, setDrag] = useState<Drag | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [newForm, setNewForm] = useState<NewForm | null>(null);
  const [openSub, setOpenSub] = useState<string | null>(null);

  const frames = timeframes(current);
  const frame = frames.find((f) => f.key === tf) ?? frames[3]!;

  useEffect(() => {
    let live = true;
    setLoadError(null);
    fetchResourcing(authenticatedFetch, frame.from, frame.to)
      .then((d) => {
        if (!live) return;
        setData(d);
        // The server's month wins: the time frames are relative to it.
        if (d.current_month && d.current_month !== current) setCurrent(d.current_month);
      })
      .catch((e: unknown) => live && setLoadError(e instanceof Error ? e.message : 'Could not load the resource plan.'));
    return () => {
      live = false;
    };
  }, [frame.from, frame.to, reload, current]);

  useEffect(() => {
    saveTab(email, { settings, timeframe: tf, collapsed: [...collapsed], expanded: [...expanded] });
  }, [email, settings, tf, collapsed, expanded]);

  /** Each tab keeps its own settings: switching restores the tab's last ones. */
  const switchView = (view: View) => {
    if (view === settings.view) return;
    const tab = loadPrefs(email).tabs[view];
    setSettings(tab.settings);
    setTf(tab.timeframe);
    setCollapsed(new Set(tab.collapsed));
    setExpanded(new Set(tab.expanded));
    setSel(null);
    setDrag(null);
  };

  const refresh = useCallback(() => setReload((n) => n + 1), []);
  const patch = (next: Partial<Settings>) => setSettings((s) => ({ ...s, ...next }));

  const planner = useMemo(() => (data ? new Planner(data, settings) : null), [data, settings]);
  const grid = useMemo(() => (planner ? buildGrid(planner) : null), [planner]);
  const metrics = useMemo(() => (planner ? buildMetrics(planner) : LOADING_METRICS), [planner]);
  const panel = planner && sel ? buildPanel(planner, sel) : null;
  const move = planner && drag ? moveTargets(planner, drag) : null;

  const go = (href: string, event: MouseEvent) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    navigate(href);
  };

  const startMove = (item: PanelItem) => {
    if (!planner || !item.move) return;
    const a = item.move.assignment;
    if (drag?.assignment.id === a.id) {
      setDrag(null);
      return;
    }
    setDrag({ assignment: a, hours: planner.shown(a) });
    setDone(null);
    setActionError(null);
  };

  const moveTo = async ({ rowKey }: { rowKey: string }) => {
    if (!planner || !drag || busy) return;
    const to = rowKey.slice(2);
    const text = movedText(planner, drag, to);
    setBusy(true);
    setActionError(null);
    try {
      const { previous } = await reassignAssignment(authenticatedFetch, drag.assignment.id, to);
      setDone({ kind: 'move', text, id: drag.assignment.id, previous: previous.app_user_id, month: drag.assignment.month });
      setSel({ type: 'person', key: to, month: drag.assignment.month });
      setDrag(null);
      refresh();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not move the assignment.');
    } finally {
      setBusy(false);
    }
  };

  /** Shows the new hours at once, saves them, then reloads the roll-ups the server rebuilt. */
  const editHours = async (id: string, hours: number) => {
    if (!data || !planner) return;
    const before = data.assignments.find((a) => a.id === id)?.hours;
    if (before === undefined || before === hours) return;
    const text = hoursText(planner, id, hours, before);
    setData(withHours(data, id, hours));
    setActionError(null);
    try {
      const { previous } = await updateAssignmentHours(authenticatedFetch, id, hours);
      setDone({ kind: 'hours', text, id, previous });
    } catch (error) {
      setData((d) => (d ? withHours(d, id, before) : d));
      setActionError(error instanceof Error ? error.message : 'Could not change the hours.');
    } finally {
      refresh();
    }
  };

  const undo = async () => {
    if (!done || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      if (done.kind === 'hours') {
        setData((d) => (d ? withHours(d, done.id, done.previous) : d));
        await updateAssignmentHours(authenticatedFetch, done.id, done.previous);
      } else if (done.kind === 'move') {
        await reassignAssignment(authenticatedFetch, done.id, done.previous);
        setSel(done.previous === null ? null : { type: 'person', key: done.previous, month: done.month });
      } else {
        for (const id of done.ids) await deleteAssignment(authenticatedFetch, id);
      }
      setDone(null);
      refresh();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not undo that.');
    } finally {
      setBusy(false);
    }
  };

  const openNew = (preset: { person?: string; role?: string } = {}) => {
    if (!data) return;
    setNewForm(defaultForm(data, preset));
  };

  const onPrimary = () => {
    if (!panel || !sel) return;
    if (panel.primary.action === 'assign-person') openNew({ person: sel.key });
    else if (panel.primary.action === 'name-someone') {
      const first = panel.sections[0]?.items.find((i) => i.move);
      if (first) startMove(first);
      else openNew({ role: sel.key });
    }
  };

  const headerKeys = grid?.headerKeys ?? [];
  const allCollapsed = headerKeys.length > 0 && headerKeys.every((k) => collapsed.has(k));
  const people = settings.view === 'people';
  const showLabels = (['confirmed', 'assigned', 'forecast'] as const).filter((k) => settings.show[k]);
  const showValue = showLabels.length === 3 ? 'All' : showLabels.length === 0 ? 'Off' : showLabels.map((k) => k[0]!.toUpperCase() + k.slice(1)).join(', ');
  const sorts = people ? PERSON_SORTS : PROJECT_SORTS;
  const sortValue = people ? settings.personSort : settings.projectSort;
  const groupOptions: [string, string][] = people ? [['role', 'Role'], ['none', 'No grouping']] : [['status', 'Active and pipeline'], ['none', 'No grouping']];
  const groupValue = people ? settings.personGroup : settings.projectGroup;

  /** One view setting: a label/value row opening its own picklist. */
  const settingRow = (key: string, title: string, value: string, body: ReactNode, hint?: string) => (
    <MenuSub open={openSub === key} onOpenChange={(open) => setOpenSub(open ? key : null)}>
      <MenuSubTrigger className="justify-between gap-3">
        <span className="text-[var(--fg)]">{title}</span>
        <span className="ml-auto max-w-40 truncate font-emphasis text-[var(--fg-strong)]">{value}</span>
      </MenuSubTrigger>
      <MenuSubContent className="w-60">
        <MenuLabel>{title}</MenuLabel>
        {hint ? <div className="px-2 pb-1 text-[11px] text-[var(--fg-muted)]">{hint}</div> : null}
        {body}
      </MenuSubContent>
    </MenuSub>
  );
  /** A single choice closes its picklist and leaves the settings open, as a row's value changes. */
  const pick = (apply: () => void) => (event: Event) => {
    event.preventDefault();
    apply();
    setOpenSub(null);
  };

  const banner = drag && planner
    ? { text: movingText(planner, drag), button: 'Cancel', action: () => setDrag(null) }
    : done ? { text: done.text, button: 'Undo', action: undo } : null;

  return (
    <PageContent as="section" width="fill" className="gap-4 px-6 pb-8 pt-5">
      <Card className="shrink-0">
        <div className="flex flex-wrap items-end justify-between gap-4 px-7 pb-[26px] pt-7">
          <div className="min-w-0">
            <div className="oly-eyebrow flex items-center gap-1.5">
              <ScheduleIcon width={12} height={12} aria-hidden />
              Delivery
            </div>
            <h1 className="m-0 mt-2.5 text-[28px] leading-tight [font-weight:var(--title-weight)] tracking-[-0.01em] text-[var(--fg-strong)]">
              Resource Planning
            </h1>
          </div>
        </div>
      </Card>

      <MetricRow
        aria-label={`${focusLabel(data?.months ?? [], settings.focus)} summary`}
        surface="inverse"
        className="shrink-0"
        metrics={metrics}
      />

      <Card className="shrink-0 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-0 border-b border-solid border-[var(--border)] px-5">
          <Tabs value={settings.view} onValueChange={(v) => switchView(v as View)}>
            <TabList aria-label="View" className="border-b-0 px-0">
              {VIEWS.map(([key, label, icon]) => <Tab key={key} value={key}>{icon}{label}</Tab>)}
            </TabList>
          </Tabs>
          <div className="flex flex-wrap items-center gap-2 py-1.5">
            {headerKeys.length > 0 ? (
              <Button
                size="icon-sm"
                aria-label={allCollapsed ? 'Expand all' : 'Collapse all'}
                title={allCollapsed ? 'Expand all' : 'Collapse all'}
                onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(headerKeys))}
              >
                {allCollapsed ? <ExpandAllGlyph /> : <CollapseAllGlyph />}
              </Button>
            ) : null}
            <Button size="icon-sm" aria-label="Print" title="Print" onClick={() => window.print?.()}>
              <PrintGlyph />
            </Button>
            <Menu>
              <MenuTrigger asChild>
                <Button size="icon-sm" aria-label={`Time frame: ${frame.label}`} title={frame.label}>
                  <CalendarGlyph />
                </Button>
              </MenuTrigger>
              <MenuContent align="end" className="w-56">
                <MenuLabel>Time frame</MenuLabel>
                <MenuRadioGroup
                  value={tf}
                  onValueChange={(value) => {
                    setTf(value as TimeframeKey);
                    patch({ focus: null });
                    setSel(null);
                    setDrag(null);
                  }}
                >
                  {frames.map((f) => <MenuRadioItem key={f.key} value={f.key}>{f.label}</MenuRadioItem>)}
                </MenuRadioGroup>
              </MenuContent>
            </Menu>
            <Menu onOpenChange={(open) => (open ? undefined : setOpenSub(null))}>
              <MenuTrigger asChild>
                <Button size="icon-sm" aria-label="View settings" title="View settings">
                  <SettingsGlyph />
                </Button>
              </MenuTrigger>
              <MenuContent align="end" className="w-72">
                <MenuLabel>View settings</MenuLabel>
                {settingRow(
                  'group',
                  'Group by',
                  groupOptions.find(([v]) => v === groupValue)?.[1] ?? '–',
                  <MenuRadioGroup value={groupValue}>
                    {groupOptions.map(([value, label]) => (
                      <MenuRadioItem
                        key={value}
                        value={value}
                        onSelect={pick(() => patch(people ? { personGroup: value as Settings['personGroup'] } : { projectGroup: value as Settings['projectGroup'] }))}
                      >
                        {label}
                      </MenuRadioItem>
                    ))}
                  </MenuRadioGroup>,
                )}
                {settingRow(
                  'sort',
                  'Sort by',
                  sorts.find(([v]) => v === sortValue)?.[1] ?? '–',
                  <MenuRadioGroup value={sortValue}>
                    {sorts.map(([value, label]) => (
                      <MenuRadioItem
                        key={value}
                        value={value}
                        onSelect={pick(() => patch(people ? { personSort: value as PersonSort } : { projectSort: value as ProjectSort }))}
                      >
                        {label}
                      </MenuRadioItem>
                    ))}
                  </MenuRadioGroup>,
                  SORT_HINT,
                )}
                {settingRow(
                  'show',
                  'Show',
                  showValue,
                  <>
                    {(['confirmed', 'assigned', 'forecast'] as const).map((k) => (
                      <MenuCheckboxItem
                        key={k}
                        checked={settings.show[k]}
                        onCheckedChange={(on) => setSettings((s) => ({ ...s, show: { ...s.show, [k]: !!on } }))}
                        onSelect={(event) => event.preventDefault()}
                      >
                        {k[0]!.toUpperCase() + k.slice(1)}
                      </MenuCheckboxItem>
                    ))}
                  </>,
                )}
                {settingRow(
                  'pipeline',
                  'Pipeline hours',
                  settings.weighted ? 'Weighted by probability' : 'As quoted',
                  <MenuRadioGroup value={settings.weighted ? 'weighted' : 'quoted'}>
                    <MenuRadioItem value="quoted" onSelect={pick(() => patch({ weighted: false }))}>As quoted</MenuRadioItem>
                    <MenuRadioItem value="weighted" onSelect={pick(() => patch({ weighted: true }))}>Weighted by probability</MenuRadioItem>
                  </MenuRadioGroup>,
                )}
              </MenuContent>
            </Menu>
            <Button size="icon-sm" aria-haspopup="dialog" aria-label="New assignment" title="New assignment" onClick={() => openNew()} disabled={!data}>
              <PlusGlyph />
            </Button>
          </div>
        </div>

        {banner ? (
          <InlineMessage tone="info" role="status" className="rounded-none border-x-0 border-t-0">
            <span className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <span>{banner.text}</span>
              <Button size="sm" variant="outline" onClick={banner.action} loading={busy && !drag}>{banner.button}</Button>
            </span>
          </InlineMessage>
        ) : null}
        {actionError ? (
          <InlineMessage tone="error" role="alert" className="rounded-none border-x-0 border-t-0">{actionError}</InlineMessage>
        ) : null}

        {loadError && !data ? (
          <div className="px-5 py-4"><InlineMessage tone="error" role="alert">{loadError}</InlineMessage></div>
        ) : !data || !grid || !planner ? (
          <div className="flex justify-center py-10"><Spinner label="Loading the plan" /></div>
        ) : !hasAnything(data) ? (
          <EmptyState
            className="py-10"
            icon={<ScheduleIcon />}
            title="No hours planned yet"
            description="Hours appear here as soon as a deal's primary quote has a revenue schedule: its hours are split across the roles its products need, as forecast. Add hours yourself with New assignment, and give people roles and their usual monthly hours on their People record."
            action={<Button variant="brand" onClick={() => openNew()}>New assignment</Button>}
          />
        ) : (
          <div className="flex flex-wrap items-stretch">
            <div className="min-w-0 flex-[999_1_640px]">
              {loadError ? <InlineMessage tone="error" role="alert" className="rounded-none border-x-0 border-t-0">{loadError}</InlineMessage> : null}
              <ResourceGrid
                months={grid.months}
                groups={grid.groups}
                variant={grid.variant}
                rowHeader={grid.rowHeader}
                selectedMonth={settings.focus}
                onMonthSelect={(key) => patch({ focus: key })}
                selectedCell={cellOf(sel)}
                onCellSelect={(ref) => {
                  const next = selectionOf(ref);
                  setSel(next && !sameSelection(sel, next) ? next : null);
                }}
                collapsedGroups={collapsed}
                onCollapsedGroupsChange={setCollapsed}
                expandedRows={expanded}
                onExpandedRowsChange={setExpanded}
                move={move}
                onMoveTo={moveTo}
                onNameClick={(row, event) => row.href && go(row.href, event)}
                footer={{ count: grid.count, legend: grid.legend }}
              />
            </div>
            {panel ? (
              <DetailsAside
                panel={panel}
                movingId={drag?.assignment.id ?? null}
                onMove={startMove}
                onPrimary={onPrimary}
                onClose={() => {
                  setSel(null);
                  setDrag(null);
                }}
                onNavigate={go}
                onEditHours={editHours}
                roles={data?.roles ?? []}
                onChanged={refresh}
              />
            ) : null}
          </div>
        )}
      </Card>

      {newForm && data ? (
        <NewAssignmentDialog
          data={data}
          initial={newForm}
          onClose={() => setNewForm(null)}
          onCreated={(ids, text) => {
            setNewForm(null);
            setDrag(null);
            setDone({ kind: 'add', text, ids });
            refresh();
          }}
        />
      ) : null}
    </PageContent>
  );
}
